import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { CapUsageBadge } from "../../components/CapUsageBadge";

import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { formatMoney, api } from "../../lib/api";
import { useReport } from "../../hooks/queries";
import { useQuery } from "@tanstack/react-query";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { RiskBadge } from "../../components/RiskBadge";
import { Copy, Printer, Download, ChevronLeft, ChevronRight, CalendarRange, CalendarDays, ArrowRight, Sparkles, Wallet as WalletIcon, Target, CreditCard, TrendingUp as TrendingUpIcon, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useState, useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { FxDriftCard } from "../../components/FxDriftCard";
import { LockedBadge } from "../../components/LockedBadge";
import { LockedPage } from "../../components/LockedPage";
import { useGatedAction } from "../../hooks/useGatedAction";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";

export default function Report() {
  const { t, lang, translateAction } = useLang();
  const [period, setPeriod] = useState("weekly");
  const [anchor, setAnchor] = useState(() => new Date().toISOString());
  const { data: report } = useReport(period, anchor);
  const pdfGate = useGatedAction("pdf_reports");
  // iter-170ac — Page-level gate. The basic report itself is now
  // catalog-gated (``basic_reports``) so plans that don't include it
  // hit a LockedPage instead of seeing data they aren't paying for.
  const basicGate = useGatedAction("basic_reports");
  // iter-170y — Advanced report. Lazy-loaded only when the user opens
  // the section; the basic report (above) loads instantly. The gate
  // blocks the open action when the user's plan doesn't include the
  // ``advanced_reports`` feature.
  const advancedGate = useGatedAction("advanced_reports");
  // iter-170ac — Global kill-switch lookup. When the operator flips
  // ``advanced_reports`` OFF in Admin → Funcionalidades we must HIDE
  // the section entirely (no teaser, no upsell). Per-user-plan locks
  // still keep the teaser visible since they're a conversion lever.
  const featureFlags = useFeatureFlags();
  const advancedGloballyEnabled = featureFlags.isEnabled("advanced_reports");
  // iter-170ab — Auto-expand when entitled, auto-collapse when locked.
  // iter-170ac — Derive from gate state directly so the eslint rule
  // ``react-hooks/set-state-in-effect`` stays clean. ``userToggle`` is
  // ``null`` until the user clicks Show/Hide; while ``null`` we mirror
  // the gate state. Once toggled the user's choice sticks.
  const [userToggle, setUserToggle] = useState(null);
  const showAdvanced = userToggle === null
    ? !advancedGate.locked && !advancedGate.isLoading
    : userToggle;
  const { data: advanced, isFetching: advancedFetching } = useQuery({
    queryKey: ["report-advanced", period, anchor],
    queryFn: async () => (
      await api.get("/report/advanced", { params: { period, start: anchor } })
    ).data,
    enabled: showAdvanced && !advancedGate.locked,
  });

  // Period stepper. For weekly we move ±7 days; for monthly we move ±1 calendar month.
  const step = (direction) => {
    const cur = new Date(anchor);
    if (period === "weekly") {
      cur.setUTCDate(cur.getUTCDate() + direction * 7);
    } else {
      cur.setUTCMonth(cur.getUTCMonth() + direction);
    }
    setAnchor(cur.toISOString());
  };

  const isCurrentPeriod = useMemo(() => {
    if (!report) return true;
    const end = new Date(report.end);
    return end >= new Date();
  }, [report]);

  const periodLabel = useMemo(() => {
    if (!report) return "";
    const s = new Date(report.start);
    const e = new Date(report.end);
    e.setUTCDate(e.getUTCDate() - 1); // inclusive last day
    if (period === "monthly") {
      return s.toLocaleString(tx(lang, "en", "es", "es"), { month: "long", year: "numeric" });
    }
    const fmt = (d) => d.toLocaleDateString(tx(lang, "en", "es", "es"), { month: "short", day: "numeric" });
    return `${fmt(s)} – ${fmt(e)} ${s.getUTCFullYear()}`;
  }, [report, period, lang]);

  const buildSummary = () => {
    if (!report) return "";
    const s = report.snapshot;
    const ccy = s.currency || "USD";
    const lines = [
      `LUMFI WEEKLY REPORT — ${new Date(report.generated_at).toLocaleString()}`,
      "",
      `Real available cash: ${formatMoney(s.real_available_cash, ccy)}`,
      `Safe to use this week: ${formatMoney(s.safe_to_use_this_week, ccy)} (${Math.round(s.safety_factor * 100)}%)`,
      `Monthly risk: ${s.risk_level.toUpperCase()}`,
      "",
      "BREAKDOWN",
      `• Current cash: ${formatMoney(s.current_cash, ccy)}`,
      `• Expected receivables (weighted): ${formatMoney(s.expected_receivables, ccy)}`,
      `• Upcoming payments (this month): ${formatMoney(s.upcoming_payments, ccy)}`,
      `• Suggested reserve: ${formatMoney(s.suggested_reserve, ccy)}`,
      `• Desired withdrawal: ${formatMoney(s.desired_withdrawal, ccy)}`,
      "",
      "PENDING RECEIVABLES",
      ...(report.pending_receivables.length
        ? report.pending_receivables.map((r) => `• ${r.client_name} — ${formatMoney(r.amount, ccy)} (${r.probability}) due ${new Date(r.expected_date).toLocaleDateString()}`)
        : ["• None"]),
      "",
      "PENDING PAYMENTS",
      ...(report.pending_payments.length
        ? report.pending_payments.map((p) => `• ${p.concept} — ${formatMoney(p.amount, ccy)} due ${new Date(p.due_date).toLocaleDateString()}`)
        : ["• None"]),
      "",
      `ACTION: ${translateAction(s.recommended_action_code, s.recommended_action_params, s.recommended_action, formatMoney, ccy)}`,
    ];
    if (report.fx_drift?.has_drift) {
      const fd = report.fx_drift;
      lines.push("", "FX DRIFT");
      lines.push(`• Net impact: ${formatMoney(fd.totals.net_drift, ccy)}`);
      lines.push(`• Receivables: ${formatMoney(fd.totals.assets_drift, ccy)}`);
      lines.push(`• Payments: ${formatMoney(fd.totals.liabilities_drift, ccy)} (positive = costs more)`);
      lines.push(`• Realized: ${formatMoney(fd.totals.realized_drift, ccy)}`);
      fd.by_currency.forEach((r) => {
        lines.push(`  ${r.currency}: ${formatMoney(r.net_drift, ccy)} net (${r.items_count} items)`);
      });
    }
    return lines.join("\n");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(buildSummary());
      toast.success(t("copied"));
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadAdvancedPDF = async () => {
    if (!report) return;
    // iter-170aa — Server-side PDF: hit the endpoint, force download
    // of the blob. We use ``api.get`` with ``responseType: blob`` so
    // the auth header is included automatically.
    try {
      const res = await api.get("/report/advanced/pdf", {
        params: { period, start: anchor },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lumfi-advanced-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(tx(lang, "Advanced PDF downloaded", "PDF avanzado descargado", "PDF avançado baixado"));
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("cap_exceeded")) {
        toast.warning(tx(lang,
          "Monthly cap reached. Future exports need upgrade.",
          "Llegaste al límite mensual. Próximos requieren upgrade.",
          "Limite mensal atingido. Próximos exigem upgrade."));
      } else if (detail.includes("feature_required")) {
        toast.error(tx(lang,
          "Your plan doesn't include advanced reports.",
          "Tu plan no incluye reportes avanzados.",
          "Seu plano não inclui relatórios avançados."));
      } else {
        toast.error(tx(lang, "Could not generate advanced PDF",
          "No se pudo generar el PDF avanzado",
          "Não foi possível gerar o PDF avançado"));
      }
    }
  };

  const downloadPDF = async () => {
    if (!report) return;
    try {
      const s = report.snapshot;
      const ccy = s.currency || "USD";
      const doc = new jsPDF({ unit: "pt", format: "letter" });
      const W = doc.internal.pageSize.getWidth();
      const M = 40;
      let y = M;

      // Header
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(15, 23, 42);
      doc.text("Lumfi Cash Pilot", M, y);
      doc.setFontSize(11);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100, 116, 139);
      doc.text(t("weekly_report"), M, y + 16);
      doc.text(new Date(report.generated_at).toLocaleString(), W - M, y + 16, { align: "right" });
      y += 36;
      doc.setDrawColor(226, 232, 240);
      doc.line(M, y, W - M, y);
      y += 18;

      // Top metrics
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(t("real_cash").toUpperCase(), M, y);
      doc.setFontSize(22);
      doc.setTextColor(15, 23, 42);
      doc.text(formatMoney(s.real_available_cash, ccy), M, y + 22);

      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.text(`${t("monthly_risk") || "Monthly risk"}: ${String(s.risk_level).toUpperCase()}`, W - M, y, { align: "right" });
      doc.setFontSize(12);
      doc.setTextColor(22, 163, 74);
      doc.text(
        `${t("safe_to_use")}: ${formatMoney(s.safe_to_use_this_week, ccy)} (${Math.round(s.safety_factor * 100)}%)`,
        W - M, y + 22, { align: "right" }
      );
      y += 44;

      // Action box
      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(M, y, W - M * 2, 46, 6, 6, "FD");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(t("recommended_action").toUpperCase(), M + 12, y + 16);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(15, 23, 42);
      const action = translateAction(s.recommended_action_code, s.recommended_action_params, s.recommended_action, formatMoney, ccy);
      const wrapped = doc.splitTextToSize(action, W - M * 2 - 24);
      doc.text(wrapped, M + 12, y + 32);
      y += 60;

      // Breakdown table
      autoTable(doc, {
        startY: y,
        head: [[t("financial_breakdown"), ""]],
        body: [
          [t("current_cash"), formatMoney(s.current_cash, ccy)],
          [t("expected_receivables"), `+${formatMoney(s.expected_receivables, ccy)}`],
          [t("upcoming_payments"), `-${formatMoney(s.upcoming_payments, ccy)}`],
          [t("suggested_reserve"), `-${formatMoney(s.suggested_reserve, ccy)}`],
          [t("desired_withdrawal"), formatMoney(s.desired_withdrawal, ccy)],
        ],
        margin: { left: M, right: M },
        styles: { font: "helvetica", fontSize: 10, cellPadding: 6, textColor: [15, 23, 42] },
        headStyles: { fillColor: [15, 23, 42], textColor: [255, 255, 255], halign: "left" },
        columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
      });
      y = doc.lastAutoTable.finalY + 18;

      // Receivables table
      autoTable(doc, {
        startY: y,
        head: [[t("nav_receivables"), t("amount"), ""]],
        body: report.pending_receivables.length
          ? report.pending_receivables.map((r) => [
              `${r.client_name}\n${new Date(r.expected_date).toLocaleDateString()} • ${r.probability}`,
              formatMoney(r.amount, ccy),
              "",
            ])
          : [["—", "", ""]],
        margin: { left: M, right: M },
        styles: { font: "helvetica", fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [22, 163, 74], textColor: [255, 255, 255] },
        columnStyles: { 1: { halign: "right", fontStyle: "bold", cellWidth: 110 }, 2: { cellWidth: 0 } },
      });
      y = doc.lastAutoTable.finalY + 12;

      // Payments table
      autoTable(doc, {
        startY: y,
        head: [[t("nav_payments"), t("amount"), ""]],
        body: report.pending_payments.length
          ? report.pending_payments.map((p) => [
              `${p.concept}\n${new Date(p.due_date).toLocaleDateString()} • ${p.type}`,
              formatMoney(p.amount, ccy),
              "",
            ])
          : [["—", "", ""]],
        margin: { left: M, right: M },
        styles: { font: "helvetica", fontSize: 9, cellPadding: 5 },
        headStyles: { fillColor: [220, 38, 38], textColor: [255, 255, 255] },
        columnStyles: { 1: { halign: "right", fontStyle: "bold", cellWidth: 110 }, 2: { cellWidth: 0 } },
      });

      // FX drift table (only if any foreign-currency items in window)
      if (report.fx_drift?.has_drift) {
        const fd = report.fx_drift;
        y = doc.lastAutoTable.finalY + 12;
        autoTable(doc, {
          startY: y,
          head: [[t("fx_drift_title"), t("fx_drift_recorded"), t("fx_drift_current"), t("fx_drift_net")]],
          body: [
            ...fd.by_currency.map((r) => {
              const rec = (r.sections.pending_receivables?.recorded_total || 0)
                + (r.sections.pending_payments?.recorded_total || 0)
                + (r.sections.realized?.recorded_total || 0);
              const cur = (r.sections.pending_receivables?.current_total || 0)
                + (r.sections.pending_payments?.current_total || 0)
                + (r.sections.realized?.current_total || 0);
              return [
                `${r.currency} → ${ccy} (${r.items_count})`,
                formatMoney(rec, ccy),
                formatMoney(cur, ccy),
                `${r.net_drift >= 0 ? "+" : ""}${formatMoney(r.net_drift, ccy)}`,
              ];
            }),
            [
              { content: t("fx_drift_net"), styles: { fontStyle: "bold" } },
              "",
              "",
              {
                content: `${fd.totals.net_drift >= 0 ? "+" : ""}${formatMoney(fd.totals.net_drift, ccy)}`,
                styles: { fontStyle: "bold", textColor: fd.totals.net_drift >= 0 ? [22, 163, 74] : [220, 38, 38] },
              },
            ],
          ],
          margin: { left: M, right: M },
          styles: { font: "helvetica", fontSize: 9, cellPadding: 5 },
          headStyles: { fillColor: [14, 165, 233], textColor: [255, 255, 255] },
          columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right", fontStyle: "bold" } },
        });
      }
      const pageH = doc.internal.pageSize.getHeight();
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text("Generated by Lumfi Cash Pilot", M, pageH - 20);

      const date = new Date(report.generated_at).toISOString().slice(0, 10);
      doc.save(`lumfi-weekly-report-${date}.pdf`);
      // iter-170n — debit the monthly PDF-export counter. Non-blocking:
      // if the cap is exhausted we toast, but we don't undo the save.
      try {
        await api.post("/report/track-pdf-export");
        toast.success(t("pdf_downloaded") || "PDF downloaded");
      } catch (capErr) {
        if (capErr?.response?.data?.detail === "cap_exceeded:pdf_exports_per_month") {
          toast.warning(
            lang === "es"
              ? "Llegaste al límite mensual de exports PDF. Te dejamos descargar este, pero los próximos requieren upgrade."
              : lang === "pt"
              ? "Atingiu o limite mensal de exports PDF. Permitimos este, mas os próximos exigem upgrade."
              : "You've reached your monthly PDF export cap. We let this one through; future ones need an upgrade.",
          );
        } else {
          toast.success(t("pdf_downloaded") || "PDF downloaded");
        }
      }
    } catch (e) {
      console.error(e);
      toast.error("Could not generate PDF");
    }
  };

  if (!report) {
    // iter-170ac — When the user lacks ``basic_reports`` the API
    // returns 402 and ``useReport`` keeps ``report`` undefined. Honor
    // the gate explicitly so the user sees the upsell splash instead
    // of a perpetual "loading…" spinner.
    if (basicGate.locked) {
      return (
        <LockedPage
          featureKey="basic_reports"
          titleEs="Reportes básicos"
          titleEn="Basic reports"
          titlePt="Relatórios básicos"
          bullets={[
            { es: "Caja disponible y caja segura semanal/mensual",
              en: "Real-available and safe-to-use cash, weekly & monthly",
              pt: "Caixa disponível e caixa segura semanal/mensal" },
            { es: "Cobros pendientes y pagos próximos consolidados",
              en: "Pending receivables & upcoming payments roll-up",
              pt: "Recebíveis pendentes e pagamentos consolidados" },
            { es: "Recomendación accionable por período",
              en: "Per-period actionable recommendation",
              pt: "Recomendação acionável por período" },
            { es: "Drift FX por moneda extranjera",
              en: "FX drift broken down by currency",
              pt: "Drift FX por moeda estrangeira" },
          ]}
        />
      );
    }
    return <Layout><div>{t("loading")}</div></Layout>;
  }
  const s = report.snapshot;
  const ccy = s.currency || "USD";

  return (
    <Layout>
      <div className="no-print">
        <PageHeader
          eyebrow={period === "monthly" ? t("report_scope_badge_monthly") : t("report_scope_badge_weekly")}
          title={period === "monthly" ? (tx(lang, "Monthly report", "Reporte mensual", "Reporte mensual")) : t("weekly_report")}
          subtitle={t("report_scope_sub")}
          testid="report-header"
          actions={
            <>
              <Button data-testid="report-copy-btn" variant="outline" size="sm" onClick={copy} className="gap-2 rounded-xl"><Copy className="h-4 w-4" /> {t("copy_summary")}</Button>
              <Button data-testid="report-pdf-btn" variant="outline" size="sm" onClick={() => pdfGate.run(downloadPDF)} className="gap-2 rounded-xl"><Download className="h-4 w-4" /> PDF<LockedBadge featureKey="pdf_reports" className="ml-1" /><CapUsageBadge resourceKey="pdf_exports_per_month" isEs={lang === "es"} /></Button>
              <Button data-testid="report-print-btn" size="sm" onClick={() => window.print()} className="bg-slate-900 hover:bg-slate-800 gap-2 rounded-xl"><Printer className="h-4 w-4" /> {t("print")}</Button>
            </>
          }
        />
      </div>

      {/* Period navigator */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 no-print w-full max-w-full">
        <div className="flex items-center gap-2 bg-slate-100 rounded-full p-1 shrink-0" data-testid="report-period-toggle">
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            data-testid="report-period-weekly"
            onClick={() => setPeriod("weekly")}
            className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors gap-1.5 inline-flex items-center ${
              period === "weekly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            <CalendarDays className="h-3.5 w-3.5" />
            {tx(lang, "Weekly", "Semanal", "Semanal")}
          </button>
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            data-testid="report-period-monthly"
            onClick={() => setPeriod("monthly")}
            className={`px-3 py-1.5 text-xs font-bold rounded-full transition-colors gap-1.5 inline-flex items-center ${
              period === "monthly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"
            }`}
          >
            <CalendarRange className="h-3.5 w-3.5" />
            {tx(lang, "Monthly", "Mensual", "Mensual")}
          </button>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="report-prev-btn" variant="outline" size="icon" onClick={() => step(-1)} className="rounded-xl">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div data-testid="report-period-label" className="text-xs sm:text-sm font-semibold text-slate-900 capitalize min-w-[120px] sm:min-w-[160px] text-center">
            {periodLabel}
          </div>
          <Button data-testid="report-next-btn" variant="outline" size="icon" onClick={() => step(1)} disabled={isCurrentPeriod} className="rounded-xl">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="print-page space-y-5">
        <Card className="p-6 rounded-2xl border-slate-200">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] font-bold text-slate-500">{t("real_cash")}</div>
              <div className="font-display text-3xl sm:text-4xl font-bold tabular-nums text-slate-900" data-testid="report-real-cash">{formatMoney(s.real_available_cash, ccy)}</div>
            </div>
            <RiskBadge risk={s.risk_level} size="lg" />
          </div>
          <div className="mt-4 grid sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-100">
              <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700">{t("safe_to_use")}</div>
              <div className="font-display text-2xl font-bold tabular-nums text-emerald-700">{formatMoney(s.safe_to_use_this_week, ccy)}</div>
              <div className="text-xs text-slate-600 mt-1">{Math.round(s.safety_factor * 100)}% {t("of_real_cash")}</div>
            </div>
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">{t("recommended_action")}</div>
              <div className="text-sm text-slate-900 mt-1">
                {translateAction(s.recommended_action_code, s.recommended_action_params, s.recommended_action, formatMoney, ccy)}
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl border-slate-200">
          <h3 className="font-display text-lg font-bold text-slate-900">{t("financial_breakdown")}</h3>
          <ul className="mt-3 divide-y divide-slate-100">
            <Row label={t("current_cash")} value={formatMoney(s.current_cash, ccy)} />
            <Row label={t("expected_receivables")} value={`+${formatMoney(s.expected_receivables, ccy)}`} color="text-emerald-700" />
            <Row label={t("upcoming_payments")} value={`-${formatMoney(s.upcoming_payments, ccy)}`} color="text-red-600" />
            <Row label={t("suggested_reserve")} value={`-${formatMoney(s.suggested_reserve, ccy)}`} />
            <Row label={t("desired_withdrawal")} value={formatMoney(s.desired_withdrawal, ccy)} />
          </ul>
        </Card>

        <div className="grid lg:grid-cols-2 gap-5">
          <Card className="p-6 rounded-2xl border-slate-200">
            <h3 className="font-display text-lg font-bold text-slate-900">{t("nav_receivables")}</h3>
            <ul className="mt-3 divide-y divide-slate-100">
              {report.pending_receivables.length === 0 && <li className="py-2 text-sm text-slate-500">None</li>}
              {report.pending_receivables.map((r) => (
                <li key={r.id} className="py-2.5 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{r.client_name}</div>
                    <div className="text-xs text-slate-500">{new Date(r.expected_date).toLocaleDateString()} • {r.probability}</div>
                  </div>
                  <div className="font-display font-bold tabular-nums">{formatMoney(r.amount, ccy)}</div>
                </li>
              ))}
            </ul>
          </Card>
          <Card className="p-6 rounded-2xl border-slate-200">
            <h3 className="font-display text-lg font-bold text-slate-900">{t("nav_payments")}</h3>
            <ul className="mt-3 divide-y divide-slate-100">
              {report.pending_payments.length === 0 && <li className="py-2 text-sm text-slate-500">None</li>}
              {report.pending_payments.map((p) => (
                <li key={p.id} className="py-2.5 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{p.concept}</div>
                    <div className="text-xs text-slate-500">{new Date(p.due_date).toLocaleDateString()} • {p.type}</div>
                  </div>
                  <div className="font-display font-bold tabular-nums">{formatMoney(p.amount, ccy)}</div>
                </li>
              ))}
            </ul>
          </Card>
        </div>

        <FxDriftCard fxDrift={report.fx_drift} baseCurrency={ccy} />

        {/* iter-170y — Advanced report: gated section that loads only
            when the user explicitly opens it.
            iter-170ac — Hide the whole card when the global kill-switch
            is OFF (admin disabled the capability for everyone). If the
            capability is globally enabled, render normally so per-plan
            locks still show the teaser/upsell. */}
        {advancedGloballyEnabled && (
        <Card className="rounded-2xl border-sky-200" data-testid="advanced-report-section">
          <div className="px-5 py-3 flex items-center justify-between border-b border-slate-100">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-slate-800">
                {tx(lang, "Advanced report", "Reporte avanzado", "Relatório avançado")}
              </h3>
              {advancedGate.locked && <LockedBadge featureKey="advanced_reports" />}
            </div>
            {!advancedGate.locked ? (
            <Button
              variant={showAdvanced ? "outline" : "default"}
              size="sm"
              onClick={() => setUserToggle((v) => !(v ?? showAdvanced))}
              data-testid="advanced-report-toggle"
            >
              {showAdvanced
                ? tx(lang, "Hide", "Ocultar", "Ocultar")
                : tx(lang, "Show", "Ver", "Ver")}
            </Button>
          ) : (
            <Button
              variant="default"
              size="sm"
              onClick={() => advancedGate.run(() => setUserToggle(true))}
              data-testid="advanced-report-locked"
              className="bg-sky-600 hover:bg-sky-700 gap-2"
            >
              {tx(lang, "Unlock", "Desbloquear", "Desbloquear")}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        {/* iter-170aa — When locked, show a teaser of what the user
            would get instead of a blank gate. Keeps the marketing
            promise visible at the moment of intent (highest-converting
            upsell slot in the product). */}
        {advancedGate.locked && (
          <div className="p-5 space-y-3" data-testid="advanced-report-teaser">
            <p className="text-sm text-slate-600 leading-snug">
              {tx(lang,
                "Unlock the advanced report to get:",
                "Desbloquea el reporte avanzado para obtener:",
                "Desbloqueie o relatório avançado para obter:")}
            </p>
            <ul className="space-y-1.5 text-sm text-slate-700">
              <li className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-sky-600 shrink-0" />
                {tx(lang,
                  "6-month income vs expense trend",
                  "Tendencia de 6 meses · ingresos vs gastos",
                  "Tendência de 6 meses · receitas vs despesas")}
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-sky-600 shrink-0" />
                {tx(lang,
                  "Top 10 income & expense categories",
                  "Top 10 categorías de ingreso y gasto",
                  "Top 10 categorias de receita e despesa")}
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-sky-600 shrink-0" />
                {tx(lang,
                  "Active goals progress + debt amortization projection",
                  "Progreso de metas activas + amortización proyectada de deudas",
                  "Progresso das metas ativas + amortização projetada das dívidas")}
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-sky-600 shrink-0" />
                {tx(lang,
                  "FX drift broken down by currency",
                  "Drift FX desglosado por moneda",
                  "Drift FX detalhado por moeda")}
              </li>
              <li className="flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-sky-600 shrink-0" />
                {tx(lang,
                  "Downloadable PDF (also emailed automatically)",
                  "PDF descargable (también se envía por email)",
                  "PDF para download (também enviado por email)")}
              </li>
            </ul>
            <div className="flex flex-wrap gap-2 pt-2">
              <Link to="/pricing?highlight=advanced_reports">
                <Button
                  size="sm"
                  className="bg-sky-600 hover:bg-sky-700 gap-1.5 rounded-full"
                  data-testid="advanced-report-upsell-cta"
                >
                  {tx(lang, "See plans with this", "Ver planes que lo incluyen", "Ver planos que incluem")}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </div>
        )}
          {showAdvanced && !advancedGate.locked && (
            <div className="p-5 space-y-6">
              {advancedFetching && !advanced && (
                <p className="text-xs text-slate-400">
                  {tx(lang, "Loading…", "Cargando…", "Carregando…")}
                </p>
              )}
              {advanced && (
                <>
                  {/* iter-188 — 6-month KPI cards. Pre-digested numbers
                      give the user the headline before they scan the
                      chart. Arrows are based on the LATEST month vs
                      the PREVIOUS one. */}
                  {(() => {
                    const trend = advanced.trend_6m || [];
                    const last = trend[trend.length - 1] || { income: 0, expense: 0, net: 0 };
                    const prev = trend[trend.length - 2] || { income: 0, expense: 0, net: 0 };
                    const sum = trend.reduce(
                      (a, r) => ({
                        income: a.income + (r.income || 0),
                        expense: a.expense + (r.expense || 0),
                        net: a.net + (r.net || 0),
                      }),
                      { income: 0, expense: 0, net: 0 },
                    );
                    const avgNet = trend.length ? sum.net / trend.length : 0;
                    const trendIncome = last.income - prev.income;
                    const trendExpense = last.expense - prev.expense;
                    const trendNet = last.net - prev.net;
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="advanced-kpis">
                        <KpiCard
                          tone="emerald"
                          label={tx(lang, "Income (6m)", "Ingresos (6m)", "Receitas (6m)")}
                          value={formatMoney(sum.income, ccy)}
                          deltaLabel={trendIncome >= 0
                            ? `+${formatMoney(Math.abs(trendIncome), ccy)} ${tx(lang, "vs prev. month", "vs mes ant.", "vs mês ant.")}`
                            : `−${formatMoney(Math.abs(trendIncome), ccy)} ${tx(lang, "vs prev. month", "vs mes ant.", "vs mês ant.")}`}
                          deltaPositive={trendIncome >= 0}
                          testid="advanced-kpi-income"
                        />
                        <KpiCard
                          tone="rose"
                          label={tx(lang, "Expense (6m)", "Gastos (6m)", "Despesas (6m)")}
                          value={formatMoney(sum.expense, ccy)}
                          deltaLabel={trendExpense >= 0
                            ? `+${formatMoney(Math.abs(trendExpense), ccy)} ${tx(lang, "vs prev. month", "vs mes ant.", "vs mês ant.")}`
                            : `−${formatMoney(Math.abs(trendExpense), ccy)} ${tx(lang, "vs prev. month", "vs mes ant.", "vs mês ant.")}`}
                          deltaPositive={trendExpense < 0}  // lower expense is good
                          testid="advanced-kpi-expense"
                        />
                        <KpiCard
                          tone="violet"
                          label={tx(lang, "Avg net / month", "Neto promedio / mes", "Líquido médio / mês")}
                          value={formatMoney(avgNet, ccy)}
                          deltaLabel={trendNet >= 0
                            ? `+${formatMoney(Math.abs(trendNet), ccy)} ${tx(lang, "this month", "este mes", "este mês")}`
                            : `−${formatMoney(Math.abs(trendNet), ccy)} ${tx(lang, "this month", "este mes", "este mês")}`}
                          deltaPositive={trendNet >= 0}
                          testid="advanced-kpi-net"
                        />
                      </div>
                    );
                  })()}

                  {/* Trend 6m — iter-170ae: visual ComposedChart sits
                      above the textual list. Bars for income (emerald)
                      and expense (rose) + line for net. Tooltip and
                      legend localized via the Recharts formatter API. */}
                  <div data-testid="advanced-trend-6m">
                    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-2">
                      {tx(lang, "Last 6 months — income vs expense",
                        "Últimos 6 meses — ingresos vs gastos",
                        "Últimos 6 meses — receitas vs despesas")}
                    </h4>
                    <div className="h-56 -ml-2" data-testid="advanced-trend-6m-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={advanced.trend_6m}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                          <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748B" }} />
                          <YAxis
                            tick={{ fontSize: 10, fill: "#64748B" }}
                            tickFormatter={(v) => {
                              const n = Math.abs(Number(v) || 0);
                              if (n >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
                              if (n >= 1_000) return `${Math.round(v / 1000)}k`;
                              return String(v);
                            }}
                          />
                          <Tooltip
                            formatter={(value, name) => [formatMoney(Number(value) || 0, ccy), name]}
                            labelStyle={{ fontSize: 11, color: "#0F172A" }}
                            contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #E2E8F0" }}
                          />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar
                            dataKey="income"
                            name={tx(lang, "Income", "Ingresos", "Receitas")}
                            fill="#10B981"
                            radius={[4, 4, 0, 0]}
                          />
                          <Bar
                            dataKey="expense"
                            name={tx(lang, "Expense", "Gastos", "Despesas")}
                            fill="#F43F5E"
                            radius={[4, 4, 0, 0]}
                          />
                          <Line
                            type="monotone"
                            dataKey="net"
                            name={tx(lang, "Net", "Neto", "Líquido")}
                            stroke="#7C3AED"
                            strokeWidth={2}
                            dot={{ r: 3, fill: "#7C3AED" }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                    <ul className="space-y-1 text-xs mt-3">
                      {advanced.trend_6m.map((row) => (
                        <li key={row.month} className="grid grid-cols-4 gap-2 tabular-nums">
                          <span className="font-mono text-slate-500">{row.month}</span>
                          <span className="text-emerald-700">+{formatMoney(row.income, ccy)}</span>
                          <span className="text-rose-700">-{formatMoney(row.expense, ccy)}</span>
                          <span className={row.net >= 0 ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>
                            = {formatMoney(row.net, ccy)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* iter-188 — Category breakdown with PROPORTION bars
                      so the user instantly sees which category
                      dominates. Each row shows the share + amount. */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <CategoryBlock
                      title={tx(lang, "Top income categories", "Top categorías de ingreso", "Top categorias de receita")}
                      tone="emerald"
                      rows={advanced.category_breakdown.top_income}
                      ccy={ccy}
                      testid="advanced-cat-income"
                    />
                    <CategoryBlock
                      title={tx(lang, "Top expense categories", "Top categorías de gasto", "Top categorias de despesa")}
                      tone="rose"
                      rows={advanced.category_breakdown.top_expense}
                      ccy={ccy}
                      testid="advanced-cat-expense"
                    />
                  </div>

                  {/* iter-188 — Wealth roll-up with icons + tone, no
                      longer a bland 4-cell grid. */}
                  <div>
                    <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-600 mb-2">
                      {tx(lang, "Wealth roll-up", "Resumen de patrimonio", "Resumo de patrimônio")}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="advanced-wealth-summary">
                      <WealthKpi
                        Icon={WalletIcon}
                        tone="amber"
                        label={tx(lang, "Cash accounts", "Cuentas", "Contas")}
                        n={advanced.cash_accounts.length}
                      />
                      <WealthKpi
                        Icon={Target}
                        tone="violet"
                        label={tx(lang, "Active goals", "Metas activas", "Metas ativas")}
                        n={advanced.goals_active.length}
                      />
                      <WealthKpi
                        Icon={CreditCard}
                        tone="rose"
                        label={tx(lang, "Active debts", "Deudas activas", "Dívidas ativas")}
                        n={advanced.debts_active.length}
                      />
                      <WealthKpi
                        Icon={TrendingUpIcon}
                        tone="emerald"
                        label={tx(lang, "Investments", "Inversiones", "Investimentos")}
                        n={advanced.investments.length}
                      />
                    </div>
                  </div>
                  {/* PDF avanzado — gated separately on advanced_reports
                      AND pdf_reports (the export itself consumes the
                      pdf_exports_per_month cap, same as the basic PDF). */}
                  <div className="flex justify-end pt-2 border-t border-slate-100">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => pdfGate.run(downloadAdvancedPDF)}
                      data-testid="advanced-report-pdf-btn"
                      className="gap-2 rounded-xl"
                    >
                      <Download className="h-4 w-4" />
                      {tx(lang, "Download advanced PDF", "Descargar PDF avanzado", "Baixar PDF avançado")}
                      <LockedBadge featureKey="pdf_reports" className="ml-1" />
                      <CapUsageBadge resourceKey="pdf_exports_per_month" isEs={lang === "es"} />
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </Card>
        )}
      </div>
    </Layout>
  );
}

function Row({ label, value, color }) {
  return (
    <li className="flex items-center justify-between py-2.5 text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`font-display font-bold tabular-nums ${color || "text-slate-900"}`}>{value}</span>
    </li>
  );
}

const TONE_BG = {
  emerald: { card: "bg-emerald-50 border-emerald-200", text: "text-emerald-700", icon: "text-emerald-600" },
  rose:    { card: "bg-rose-50 border-rose-200",       text: "text-rose-700",    icon: "text-rose-600" },
  violet:  { card: "bg-sky-50 border-sky-200",   text: "text-sky-700",  icon: "text-sky-600" },
  amber:   { card: "bg-amber-50 border-amber-200",     text: "text-amber-700",   icon: "text-amber-600" },
};

/**
 * iter-188 — Headline KPI card for the advanced report's 6-month
 * roll-up. Big number on top + tiny delta vs previous month below.
 */
function KpiCard({ tone, label, value, deltaLabel, deltaPositive, testid }) {
  const t = TONE_BG[tone] || TONE_BG.emerald;
  const DeltaIcon = deltaPositive ? ArrowUpRight : ArrowDownRight;
  return (
    <div className={`rounded-xl border p-3 ${t.card}`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-wider font-semibold ${t.text}`}>
        {label}
      </div>
      <div className={`text-xl font-display font-bold tabular-nums ${t.text} mt-0.5`}>
        {value}
      </div>
      {deltaLabel && (
        <div className={`flex items-center gap-1 mt-1 text-[10px] ${deltaPositive ? "text-emerald-700" : "text-rose-700"}`}>
          <DeltaIcon className="h-3 w-3" />
          {deltaLabel}
        </div>
      )}
    </div>
  );
}

/**
 * iter-188 — Category breakdown with proportional fill-bars so the
 * dominating category jumps out visually. Each row is "label · bar ·
 * amount". The bar width is the row's share of the LARGEST row, not
 * the total — that way small categories don't disappear visually.
 */
function CategoryBlock({ title, tone, rows, ccy, testid }) {
  const t = TONE_BG[tone] || TONE_BG.emerald;
  const max = rows.reduce((m, r) => Math.max(m, r.amount || 0), 0) || 1;
  return (
    <div data-testid={testid}>
      <h4 className={`text-[11px] font-semibold uppercase tracking-wider ${t.text} mb-2`}>
        {title}
      </h4>
      {rows.length === 0 ? (
        <p className="text-xs text-slate-400 italic">—</p>
      ) : (
        <ul className="space-y-1.5 text-xs">
          {rows.map((r) => {
            const pct = Math.max(8, Math.round((r.amount / max) * 100));
            return (
              <li key={r.label} className="space-y-0.5">
                <div className="flex justify-between tabular-nums">
                  <span className="text-slate-700 truncate pr-2">{r.label}</span>
                  <span className="font-mono">{formatMoney(r.amount, ccy)}</span>
                </div>
                <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className={`h-full ${tone === "emerald" ? "bg-emerald-400" : tone === "rose" ? "bg-rose-400" : "bg-slate-400"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * iter-188 — Wealth KPI card variant with icon + tone. Visually richer
 * than the previous plain 4-cell grid.
 */
function WealthKpi({ Icon, tone, label, n }) {
  const t = TONE_BG[tone] || TONE_BG.amber;
  return (
    <div className={`rounded-xl border p-3 ${t.card}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`h-3.5 w-3.5 ${t.icon}`} />
        <span className={`text-[10px] uppercase tracking-wider font-semibold ${t.text}`}>
          {label}
        </span>
      </div>
      <div className={`text-xl font-display font-bold tabular-nums ${t.text}`}>
        {n}
      </div>
    </div>
  );
}
